import { create } from 'zustand';
import { persist, createJSONStorage } from 'zustand/middleware';
import { AppFeatures } from './features';
import { STORAGE_KEYS } from '@/constants';
import { v4 as uuid } from 'uuid';
import { syncService } from './syncService';

// Extract types from AppFeatures
type Settings = AppFeatures['settings'];
type Task = AppFeatures['tasks'][number];
type Timer = AppFeatures['timer'];
type Mode = Timer['current_mode'];
type SessionData = Timer['sessions'][number];
type CurrentSession = AppFeatures['CurrentSession'];

type TaskStats = {
    totalTasks: number;
    completedTasks: number;
    totalPomodorosEstimated: number;
    totalPomodorosCompleted: number;
    completionRate: number;
};

type ComputedStatistics = {
    streak: number;
    totalHours: number;
    daysAccessed: number;
    totalPomodoros: number;
    totalBreaks: number;
    averageSessionTime: number;
};





const ENABLE_PERSIST = false;

// Initialize defaults from AppFeatures
const appFeatures = new AppFeatures();

// Helper: Check if sync is enabled
const isSyncEnabled = () => {
    return !!localStorage.getItem('pomozid_token');
};

// Store State Interface
interface AppState {
    settings: Settings;
    timer: Timer;
    currentSession: CurrentSession | null;
    tasks: Task[];

    isLoading: boolean;
    isInitialized: boolean;
}

// Store Actions Interface
interface AppActions {
    updateSettings: (newSettings: Partial<Settings>) => void;
    resetSettings: () => void;
    startTimer: () => void;
    pauseTimer: () => void;
    toggleTimer: () => void;
    resetTimer: () => void;
    switchMode: (mode: Mode, saveSession?: boolean) => void;
    updateTimerState: (updates: Partial<Timer>) => void;
    tick: () => void;
    completePomodoro: () => void;
    addSession: (session: SessionData) => void;
    startSession: (mode: Mode, totalDuration: number, timeLeft: number) => void;
    endSession: (completed: boolean) => SessionData | null;
    updateSessionTime: (newTimeLeft: number, totalDuration: number) => void;
    addTask: (title: string, pomodorosEstimated: number) => Task;
    updateTask: (id: string, updates: Partial<Task>) => void;
    deleteTask: (id: string) => void;
    incrementTaskPomodoro: (id: string) => void;
    clearTasks: () => void;
    clearCompletedTasks: () => void;
    setLoading: (loading: boolean) => void;
    initialize: () => Promise<void>;
    resetAll: () => void;
}

type AppStore = AppState & AppActions;

// Helper: Get mode time in seconds
const getModeTime = (mode: Mode, settings: Settings): number => {
    const modeMap = {
        pomodoro: settings.pomodoro.time,
        shortBreak: settings.shortBreak.time,
        longBreak: settings.longBreak.time
    };
    return (modeMap[mode] || settings.pomodoro.time) * 60;
};

// Helper: Sync current state to backend
const syncCurrentState = (get: () => AppStore) => {
    if (isSyncEnabled()) {
        const appStore = get();
        syncService.syncAll(buildSyncPayload(appStore));
    }
};

// Helper: Get next mode
const getNextMode = (currentMode: Mode, completedPomodoros: number, longBreakInterval: number): Mode => {
    if (currentMode === "pomodoro") {
        return completedPomodoros % longBreakInterval === 0 ? "longBreak" : "shortBreak";
    }
    return "pomodoro";
};

// Helper: Calculate progress
const calculateProgress = (timeLeft: number, totalTime: number): number => {
    if (totalTime <= 0) return 0;
    const elapsed = totalTime - timeLeft;
    return Math.max(0, Math.min(100, (elapsed / totalTime) * 100));
};

// Helper: Get current date string
const getCurrentDate = (): string => new Date().toISOString().split('T')[0];

// Helper: Get date from timestamp
const getDateString = (timestamp: Date | number | string): string => {
    let date: Date;

    if (timestamp instanceof Date) {
        date = timestamp;
    } else if (typeof timestamp === 'number') {
        date = new Date(timestamp);
    } else if (typeof timestamp === 'string') {
        date = new Date(timestamp);
    } else {
        date = new Date();
    }

    if (isNaN(date.getTime())) {
        return getCurrentDate();
    }

    return date.toISOString().split('T')[0];
};

// Helper: Calculate streak from sessions
const calculateStreak = (sessions: SessionData[]): number => {
    if (sessions.length === 0) return 0;

    const completedPomodoros = sessions.filter(
        session => session.mode === 'pomodoro' && session.completed
    );

    if (completedPomodoros.length === 0) return 0;

    const datesWithPomodoros = Array.from(
        new Set(completedPomodoros.map(session => getDateString(session.timestamp)))
    ).sort().reverse();

    if (datesWithPomodoros.length === 0) return 0;

    const today = getCurrentDate();
    let streak = 0;

    const mostRecentDate = datesWithPomodoros[0];
    const daysDiff = getDaysDifference(mostRecentDate, today);

    if (daysDiff > 1) return 0;

    let expectedDate = new Date(mostRecentDate);

    for (const dateStr of datesWithPomodoros) {
        const expectedDateStr = expectedDate.toISOString().split('T')[0];

        if (dateStr === expectedDateStr) {
            streak++;
            expectedDate.setDate(expectedDate.getDate() - 1);
        } else {
            break;
        }
    }

    return streak;
};

// Helper: Calculate days difference
const getDaysDifference = (date1: string, date2: string): number => {
    const d1 = new Date(date1);
    const d2 = new Date(date2);
    const diffTime = Math.abs(d2.getTime() - d1.getTime());
    return Math.floor(diffTime / (1000 * 60 * 60 * 24));
};

// Helper: Calculate all streaks and find longest
const computeTaskStats = (tasks: Task[]): TaskStats => {
    const completedTasks = tasks.filter((task) => task.completed).length;
    const totalPomodorosEstimated = tasks.reduce(
        (sum, task) => sum + task.pomodorosEstimated,
        0
    );
    const totalPomodorosCompleted = tasks.reduce(
        (sum, task) => sum + task.pomodorosCompleted,
        0
    );

    return {
        totalTasks: tasks.length,
        completedTasks,
        totalPomodorosEstimated,
        totalPomodorosCompleted,
        completionRate: tasks.length > 0 ? (completedTasks / tasks.length) * 100 : 0
    };
};





const buildSyncPayload = (state: AppStore) => {
    return {
        settings: state.settings,
        timer: state.timer,
        tasks: state.tasks,
    };
};

// Store implementation
const storeImplementation = (
    set: (partial: AppStore | Partial<AppStore> | ((state: AppStore) => AppStore | Partial<AppStore>)) => void,
    get: () => AppStore
): AppStore => ({
    // Initial State
    settings: appFeatures.settings,
    timer: appFeatures.timer,
    currentSession: null,
    tasks: appFeatures.tasks,
    isLoading: false,
    isInitialized: false,

    // Settings Actions
    updateSettings: (newSettings: Partial<Settings>) => {
        set((state: AppStore) => {
            const updatedSettings = { ...state.settings, ...newSettings };
            const newTimeLeft = getModeTime(state.timer.current_mode, updatedSettings);

            return {
                settings: updatedSettings,
                timer: {
                    ...state.timer,
                    timeLeft: state.timer.isRunning ? state.timer.timeLeft : newTimeLeft
                }
            };
        });

        // Sync on settings change
        syncCurrentState(get);
    },

    resetSettings: () => {
        set({ settings: appFeatures.settings });
        syncCurrentState(get);
    },

    // Timer Actions
    startTimer: () => {
        const { timer, settings, currentSession, startSession } = get();

        if (!currentSession) {
            startSession(
                timer.current_mode,
                getModeTime(timer.current_mode, settings),
                timer.timeLeft
            );
        }

        set((state: AppStore) => ({
            timer: { ...state.timer, isRunning: true }
        }));

        // Start periodic sync (every minute) only while running
        if (isSyncEnabled()) {
            syncService.startAutoSync(
                () => buildSyncPayload(get()),
                () => get().timer.isRunning
            );
        }
    },

    pauseTimer: () => {
        set((state: AppStore) => ({
            timer: { ...state.timer, isRunning: false }
        }));

        // ✅ Sync current state before stopping auto-sync
        syncCurrentState(get);
        syncService.stopAutoSync();
    },

    toggleTimer: () => {
        const { timer } = get();
        if (timer.isRunning) {
            get().pauseTimer();
        } else {
            get().startTimer();
        }
    },

    resetTimer: () => {
        const { timer, settings, endSession, addSession } = get();

        const session = endSession(false);
        if (session) {
            addSession(session);
        }

        const newTimeLeft = getModeTime(timer.current_mode, settings);

        set((state: AppStore) => ({
            timer: {
                ...state.timer,
                isRunning: false,
                timeLeft: newTimeLeft,
                progress: 0
            }
        }));

        // ✅ Sync after reset
        syncCurrentState(get);
        syncService.stopAutoSync();
    },

    switchMode: (mode: Mode) => {
        const { settings, endSession, addSession } = get();

        const session = endSession(false);
        if (session) {
            addSession(session);
        }

        const newTimeLeft = getModeTime(mode, settings);

        set((state: AppStore) => ({
            timer: {
                ...state.timer,
                current_mode: mode,
                timeLeft: newTimeLeft,
                isRunning: false,
                progress: 0
            }
        }));

        // ✅ Sync after mode switch
        syncCurrentState(get);
    },

    updateTimerState: (updates: Partial<Timer>) => {
        set((state: AppStore) => ({
            timer: { ...state.timer, ...updates }
        }));
    },

    tick: () => {
        const { timer, settings, completePomodoro, updateSessionTime } = get();

        if (!timer.isRunning || timer.timeLeft <= 0) return;

        const newTimeLeft = timer.timeLeft - 1;
        const totalTime = getModeTime(timer.current_mode, settings);
        const progress = calculateProgress(newTimeLeft, totalTime);

        updateSessionTime(newTimeLeft, totalTime);

        set((state: AppStore) => ({
            timer: {
                ...state.timer,
                timeLeft: newTimeLeft,
                progress
            }
        }));

        if (newTimeLeft === 0) {
            completePomodoro();
        }
    },

    completePomodoro: () => {
        const { timer, settings, endSession } = get();

        const session = endSession(true);
        if (session) {
            get().addSession(session);





        }

        let nextMode = timer.current_mode;
        let newCompletedCount = timer.completedPomodoros;

        if (timer.current_mode === "pomodoro") {
            newCompletedCount += 1;
            nextMode = getNextMode(timer.current_mode, newCompletedCount, settings.longBreakInterval);
        } else {
            nextMode = "pomodoro";
        }

        const newTimeLeft = getModeTime(nextMode, settings);
        const shouldAutoStart =
            (nextMode !== "pomodoro" && settings.autoStartBreaks) ||
            (nextMode === "pomodoro" && settings.autoStartPomodoros);

        set((state: AppStore) => ({
            timer: {
                ...state.timer,
                current_mode: nextMode,
                timeLeft: newTimeLeft,
                completedPomodoros: newCompletedCount,
                isRunning: shouldAutoStart,
                progress: 0
            }
        }));

        // Sync when a pomodoro completes
        syncCurrentState(get);
    },

    addSession: (session: SessionData) => {
        set((state: AppStore) => ({
            timer: {
                ...state.timer,
                sessions: [...state.timer.sessions, session]
            }
        }));
    },

    // Session Actions
    startSession: (mode: Mode, totalDuration: number, timeLeft: number) => {
        set({
            currentSession: {
                id: uuid(),
                startTime: Date.now(),
                time: totalDuration - timeLeft,
                mode
            }
        });
    },

    endSession: (completed: boolean) => {
        const { currentSession } = get();

        if (!currentSession) return null;

        const MIN_SESSION_DURATION = 5;

        if (currentSession.time <= MIN_SESSION_DURATION && !completed) {
            set({ currentSession: null });
            return null;
        }

        const session: SessionData = {
            id: currentSession.id,
            time: currentSession.time,
            mode: currentSession.mode,
            completed,
            timestamp: new Date()
        };

        set({ currentSession: null });
        return session;
    },

    updateSessionTime: (newTimeLeft: number, totalDuration: number) => {
        set((state: AppStore) => {
            if (!state.currentSession) return state;

            return {
                currentSession: {
                    ...state.currentSession,
                    time: totalDuration - newTimeLeft
                }
            };
        });
    },

    // Task Actions
    addTask: (title: string, pomodorosEstimated: number) => {
        const newTask: Task = {
            id: Date.now().toString(),
            title: title.trim(),
            completed: false,
            pomodorosEstimated,
            pomodorosCompleted: 0,
            createdAt: Date.now()
        };

        set((state: AppStore) => ({
            tasks: [...state.tasks, newTask]
        }));

        syncCurrentState(get);

        return newTask;
    },

    updateTask: (id: string, updates: Partial<Task>) => {
        set((state: AppStore) => ({
            tasks: state.tasks.map((task) =>
                task.id === id
                    ? {
                        ...task,
                        ...updates,
                        ...(updates.completed && !task.completed
                            ? { completedAt: Date.now() }
                            : {})
                    }
                    : task
            )
        }));

        syncCurrentState(get);
    },

    deleteTask: (id: string) => {
        set((state: AppStore) => ({
            tasks: state.tasks.filter((task) => task.id !== id)
        }));

        syncCurrentState(get);
    },

    incrementTaskPomodoro: (id: string) => {
        set((state: AppStore) => ({
            tasks: state.tasks.map((task) =>
                task.id === id && task.pomodorosCompleted < task.pomodorosEstimated
                    ? { ...task, pomodorosCompleted: task.pomodorosCompleted + 1 }
                    : task
            )
        }));

        syncCurrentState(get);
    },

    clearTasks: () => {
        set({ tasks: [] });
        syncCurrentState(get);
    },

    clearCompletedTasks: () => {
        set((state: AppStore) => ({
            tasks: state.tasks.filter((task) => !task.completed)
        }));

        syncCurrentState(get);
    },





    // Utility Actions
    setLoading: (loading: boolean) => {
        set({ isLoading: loading });
    },

    // Initialize with backend sync
    initialize: async () => {
        const currentDate = getCurrentDate();

        // Try to fetch data from backend if logged in
        if (isSyncEnabled()) {
            try {
                console.log('🔄 Loading data from Firestore...');
                const resData: any = await syncService.fetchUserData();
                const backendData = resData?.data.data || null;

                if (backendData) {
                    // Load data from backend
                    set((state: AppStore) => ({
                        settings: backendData.settings || state.settings,
                        tasks: backendData.tasks || state.tasks,

                        timer: {
                            ...state.timer,
                            // ✅ Restore all timer state from backend
                            timeLeft: backendData.timer?.timeLeft ?? state.timer.timeLeft,
                            current_mode: backendData.timer?.current_mode || state.timer.current_mode,
                            isRunning: false, // Always start paused for safety
                            progress: backendData.timer?.progress ?? state.timer.progress,
                            sessions: backendData.timer?.sessions || state.timer.sessions,
                            completedPomodoros: backendData.timer?.completedPomodoros || state.timer.completedPomodoros,
                            currentDate
                        },
                        isInitialized: true
                    }));

                    console.log('✅ Loaded data from Firestore');

                    // Start auto-sync after loading
                    syncService.startAutoSync(
                        () => buildSyncPayload(get()),
                        () => get().timer.isRunning
                    );

                    return;
                } else {
                    console.log('ℹ️ No data in Firestore yet - performing initial sync');

                    // Perform immediate sync with current state
                    const currentState = get();
                    const syncSuccess = await syncService.syncAll(
                        buildSyncPayload(currentState)
                    );

                    if (syncSuccess) {
                        console.log('✅ Initial sync to Firestore completed');
                    } else {
                        console.warn('⚠️ Initial sync failed');
                    }

                    // Start auto-sync
                    syncService.startAutoSync(
                        () => buildSyncPayload(get()),
                        () => get().timer.isRunning
                    );
                }
            } catch (error) {
                console.error('❌ Failed to load from backend:', error);
            }
        }

        // Fallback to local initialization
        set((state: AppStore) => {
            if (state.timer.currentDate !== currentDate) {
                return {
                    timer: {
                        ...appFeatures.timer,
                        timeLeft: getModeTime('pomodoro', state.settings),
                        currentDate,
                        sessions: state.timer.sessions
                    },
                    isInitialized: true
                };
            }

            return { isInitialized: true };
        });
    },

    resetAll: () => {
        set({
            settings: appFeatures.settings,
            timer: appFeatures.timer,
            currentSession: null,
            tasks: appFeatures.tasks,
            isLoading: false
        });
        syncCurrentState(get);
    }
});

// Create the Zustand store
export const useAppStore = create<AppStore>()(
    ENABLE_PERSIST
        ? persist(storeImplementation, {
            name: STORAGE_KEYS.APP_STATE,
            storage: createJSONStorage(() => localStorage),
            partialize: (state) => ({
                settings: state.settings,
                timer: state.timer,
                tasks: state.tasks,

            })
        })
        : storeImplementation
);

// Selectors for optimized access
export const useSettings = () => useAppStore((state) => state.settings);
export const useTimer = () => useAppStore((state) => state.timer);
export const useTasks = () => useAppStore((state) => state.tasks);

// Computed selectors
export const activeTasks = () =>
    useAppStore((state) => state.tasks.filter((task) => !task.completed));

export const completedTasks = () =>
    useAppStore((state) => state.tasks.filter((task) => task.completed));

export const useTaskStats = (): TaskStats => {
    const tasks = useAppStore((state) => state.tasks);

    return computeTaskStats(tasks);
};

// Computed Statistics Selector
export const useComputedStatistics = (): ComputedStatistics => {
    const sessions = useAppStore((state) => state.timer.sessions);


    const totalHours = sessions.reduce((total, session) => {
        return total + (session.time / 3600);
    }, 0);

    const uniqueDates = new Set(
        sessions.map(session => getDateString(session.timestamp))
    );
    const daysAccessed = uniqueDates.size;

    const totalPomodoros = sessions.filter(
        session => session.mode === 'pomodoro' && session.completed
    ).length;

    const totalBreaks = sessions.filter(
        session => (session.mode === 'shortBreak' || session.mode === 'longBreak') && session.completed
    ).length;

    const averageSessionTime = sessions.length > 0
        ? sessions.reduce((sum, session) => sum + session.time, 0) / sessions.length / 60
        : 0;

    const currentStreak = calculateStreak(sessions);



    return {
        streak: currentStreak,
        totalHours: Math.round(totalHours * 100) / 100,
        daysAccessed,
        totalPomodoros,
        totalBreaks,
        averageSessionTime: Math.round(averageSessionTime * 10) / 10,

    };
};

export const useStatistics = useComputedStatistics;

// Export hook to manage sync
export const useSyncManager = () => {
    const startSync = () => {
        syncService.startAutoSync(
            () => buildSyncPayload(useAppStore.getState()),
            () => useAppStore.getState().timer.isRunning
        );
    };

    const stopSync = () => {
        syncService.stopAutoSync();
    };

    const manualSync = async () => {
        return await syncService.syncAll(
            buildSyncPayload(useAppStore.getState())
        );
    };

    return { startSync, stopSync, manualSync };
};